import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { authService } from '../services/auth'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [permissions, setPermissions] = useState([])
  const [loading, setLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const navigate = useNavigate()

  // Fetch user permissions
  const fetchPermissions = useCallback(async () => {
    try {
      const permissionsData = await authService.getUserPermissions()
      setPermissions(permissionsData.permissions || [])
    } catch (error) {
      console.error('Failed to fetch permissions:', error)
      setPermissions([])
    }
  }, [])

  // Check if user has a specific permission
  const hasPermission = (permissionCode) => {
    if (!user) return false
    if (user.is_superuser) return true
    return permissions.includes(permissionCode)
  }

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token')
      if (!token) {
        setLoading(false)
        return
      }

      const userData = await authService.getCurrentUser()
      if (userData) {
        setUser(userData)
        setIsAuthenticated(true)
        // Fetch permissions after user data is loaded
        await fetchPermissions()
      }
    } catch (error) {
      console.error('Auth check failed:', error)
      // Clear invalid token
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
    } finally {
      setLoading(false)
    }
  }, [fetchPermissions])

  // Check if user is logged in on mount
  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  const login = async (email, password) => {
    try {
      const response = await authService.login(email, password)

      // Store tokens
      localStorage.setItem('access_token', response.tokens.access_token)
      localStorage.setItem('refresh_token', response.tokens.refresh_token)

      // Set user data
      setUser(response.user)
      setIsAuthenticated(true)

      // Fetch user permissions
      await fetchPermissions()

      // Show success message
      toast.success('Login successful!')

      // Navigate to profile/dashboard
      navigate('/profile')

      return { success: true }
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed'
      toast.error(message)
      return { success: false, error: message }
    }
  }

  const register = async (email, password, fullName) => {
    try {
      const response = await authService.register(email, password, fullName)

      // Store tokens
      localStorage.setItem('access_token', response.tokens.access_token)
      localStorage.setItem('refresh_token', response.tokens.refresh_token)

      // Set user data
      setUser(response.user)
      setIsAuthenticated(true)

      // Fetch user permissions
      await fetchPermissions()

      // Show success message
      toast.success('Registration successful! Please verify your email.')

      // Navigate to profile
      navigate('/profile')

      return { success: true }
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed'
      toast.error(message)
      return { success: false, error: message }
    }
  }

  const logout = async () => {
    try {
      await authService.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      // Clear local storage
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('user')

      // Clear state
      setUser(null)
      setPermissions([])
      setIsAuthenticated(false)

      // Show message
      toast.success('Logged out successfully')

      // Navigate to home
      navigate('/')
    }
  }

  const updateProfile = async (data) => {
    try {
      const updatedUser = await authService.updateProfile(data)
      setUser(updatedUser)
      toast.success('Profile updated successfully')
      return { success: true }
    } catch (error) {
      const message = error.response?.data?.detail || 'Update failed'
      toast.error(message)
      return { success: false, error: message }
    }
  }

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await authService.changePassword(currentPassword, newPassword)
      toast.success('Password changed successfully')
      return { success: true }
    } catch (error) {
      const message = error.response?.data?.detail || 'Password change failed'
      toast.error(message)
      return { success: false, error: message }
    }
  }

  const resetPassword = async (email) => {
    try {
      await authService.resetPassword(email)
      toast.success('Password reset link sent to your email')
      return { success: true }
    } catch (error) {
      const message = error.response?.data?.detail || 'Reset request failed'
      toast.error(message)
      return { success: false, error: message }
    }
  }

  const oauthLogin = async (provider) => {
    try {
      // Get OAuth URL from backend
      const authUrl = await authService.getOAuthUrl(provider)

      // Redirect to OAuth provider
      window.location.href = authUrl

      return { success: true }
    } catch (error) {
      const message = error.response?.data?.detail || `${provider} login failed`
      toast.error(message)
      return { success: false, error: message }
    }
  }

  const verifyEmail = async (token) => {
    try {
      await authService.verifyEmail(token)

      // Update user verification status
      if (user) {
        setUser({ ...user, is_verified: true })
      }

      toast.success('Email verified successfully!')
      return { success: true }
    } catch (error) {
      const message = error.response?.data?.detail || 'Verification failed'
      toast.error(message)
      return { success: false, error: message }
    }
  }

  const value = {
    user,
    permissions,
    loading,
    isAuthenticated,
    hasPermission,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    resetPassword,
    oauthLogin,
    verifyEmail,
    checkAuth
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
